import { AstraDropdownItem } from '../../../AstraDropdownMenu/AstraDropdownItem'
import { TimePickerProps, TimeSelection } from './types'
import { prefixValue } from './utils'

type AstraTimePickerMenuProps = {
    timeSelection: TimeSelection
    hourSelected?: number
    minuteSelected?: number
    onTimeSelect?: (hour: number, minute: number) => void
} & Pick<TimePickerProps, 'minTime' | 'maxTime'>

/**
 * Dropdown menu for time selection
 *
 * This component displays a dropdown menu with hour and minute options.
 * @returns {JSX.Element}
 */
export const AstraTimePickerSelectionMenu = ({ timeSelection, hourSelected, minuteSelected, onTimeSelect, minTime, maxTime }: AstraTimePickerMenuProps) => {
    const { hours, minutes } = timeSelection

    const isValidHour = (hour?: string | number) => {
        // If no minTime or maxTime is set, all hours are valid
        if (!minTime && !maxTime) {
            return true
        }

        const hourValue = Number(hour)
        const minHour = minTime?.hour() || 0
        const maxHour = maxTime?.hour() || 23

        return hourValue >= minHour && hourValue <= maxHour
    }

    const isValidMinute = (minute?: string | number, hour?: number | string) => {
        // If no minTime or maxTime is set, all minutes are valid
        if (!minTime && !maxTime) {
            return true
        }

        // No hour selected, so we don't need to validate minutes
        if (hour && !isValidHour(hour)) {
            return true
        }

        const minuteValue = Number(minute)
        const hourValue = Number(hour)

        if (hourValue === minTime?.hour() && minuteValue < minTime.minute()) {
            return false
        } else if (hourValue === maxTime?.hour() && minuteValue > maxTime.minute()) {
            return false
        } else {
            return true
        }
    }

    const onTimeSelected = (hour: number, minute: number) => {
        onTimeSelect?.(hour, minute)
    }

    return (
        <div className="max-h-56 w-full items-center overflow-y-auto scroll-smooth pl-1 pr-1">
            {hours.map((hour, hourIndex) =>
                minutes.map(
                    (minute, minuteIndex) =>
                        isValidHour(hour) &&
                        isValidMinute(minute, hour) && (
                            <AstraDropdownItem
                                key={`${hourIndex}-${minuteIndex}`}
                                option={{
                                    type: 'action',
                                    title: `${prefixValue(hour)} : ${prefixValue(minute)}`,
                                    isActive: hourSelected === hour && minuteSelected === minute,
                                    action: () => {
                                        onTimeSelected(hour, minute)
                                    },
                                }}
                            />
                        )
                )
            )}
        </div>
    )
}
